
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const GeometricTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  // Use simple distinct colors for the geometric look
  const primary = profile.brandColor;
  const secondary = '#1e293b'; // Slate 800

  return (
    <div className={containerClass}>
       {!hideHeader && (
         <>
           {/* Bauhaus Grid Header */}
           <div className="grid grid-cols-12 h-64">
               <div className="col-span-8 bg-neutral-50 p-12 flex flex-col justify-center">
                   <h1 className="text-4xl font-bold text-neutral-900 tracking-tight mb-2">{profile.name}</h1>
                   <p className="text-neutral-500 font-medium">{profile.email}</p>
               </div>
               <div className="col-span-4 bg-neutral-900 flex items-center justify-center">
                   <h2 className="text-white text-3xl font-bold uppercase tracking-widest -rotate-90">{type}</h2>
               </div>
               <div className="col-span-2 h-4" style={{ backgroundColor: primary }}></div>
               <div className="col-span-2 h-4 bg-neutral-300"></div>
               <div className="col-span-8 h-4 bg-neutral-100"></div>
           </div>

           <div className="p-12 flex-1 flex flex-col">
               <div className="flex gap-12 mb-16">
                   <div className="flex-1">
                       <div className="w-8 h-1 bg-neutral-900 mb-4"></div>
                       <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2">Prepared For</p>
                       {client ? (
                           <div>
                               <h3 className="text-2xl font-bold text-neutral-900">{client.name}</h3>
                               <p className="text-neutral-500 font-medium">{client.company}</p>
                               <p className="text-neutral-400 text-sm mt-1">{client.address}</p>
                           </div>
                       ) : <span className="text-neutral-300">N/A</span>}
                   </div>

                   <div className="w-1/3">
                       <div className="w-8 h-1 bg-neutral-300 mb-4"></div>
                       <p className="text-xs font-bold text-neutral-400 uppercase tracking-wider mb-2">Details</p>
                       <div className="space-y-2">
                           {metaData.map((m, i) => (
                               <div key={i} className="flex justify-between items-center border-b border-neutral-100 pb-1">
                                   <span className="text-xs font-bold text-neutral-500 uppercase">{m.label}</span>
                                   <span className="text-sm font-bold text-neutral-900">{m.value}</span>
                               </div>
                           ))}
                       </div>
                   </div>
               </div>
           </div>
         </>
       )}

       <div className="p-12 flex-1 flex flex-col">
           <div className="flex-1 geometric-table">
               {children}
           </div>

           {/* Geometric Footer */}
           <div className="mt-auto pt-12">
               <div className="bg-neutral-50 p-8 border-l-[12px]" style={{ borderColor: primary }}>
                   <p className="font-bold text-neutral-900 text-sm mb-1">{footerNote}</p>
                   <p className="text-xs text-neutral-400 uppercase tracking-wider">{title}</p>
               </div>
           </div>
       </div>
    </div>
  );
};

export default GeometricTemplate;
